import { useEffect, useState, useCallback } from 'react';
import { AlertCircle, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Line, LineChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { t } from '@/i18n';
import { useFxApi } from '@/services/api';
import type { FxRatesResponse } from '@ip/shared-types';

// Widget currencies, in display order. Backend accepts more via ?codes=, but
// the dashboard only surfaces the two the business actually invoices in.
const CODES = ['EUR', 'USD'] as const;

function formatRate(v: number): string {
  return v.toLocaleString('pl-PL', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('pl-PL');
}

/**
 * Rate-change chip: NBP rate going UP means PLN weakened against the
 * currency, which is unfavourable for a PLN-reporting business — so "up" is
 * amber/rose, "down" (PLN strengthened) is emerald. This is the mirror image
 * of a typical revenue MoM chip, hence the separate component.
 */
function ChangeChip({ value }: { value: number }) {
  const magnitude = formatRate(Math.abs(value));
  if (value === 0) {
    return (
      <span
        className="bg-muted text-muted-foreground inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums"
        title={t('fx.change30dLabel')}
      >
        {magnitude}
      </span>
    );
  }
  const up = value > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex w-fit items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums ${
        up
          ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      }`}
      title={t('fx.change30dLabel')}
    >
      <Icon aria-hidden="true" className="size-3" />
      {magnitude}
    </span>
  );
}

function CurrencyPanel({ code, entry }: { code: string; entry: FxRatesResponse[string] }) {
  const config: ChartConfig = {
    rate: { label: code, color: 'var(--chart-1)' },
  };
  const chartData = entry.series.map((p) => ({ date: p.date, rate: p.rate }));

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-muted-foreground text-xs font-medium">{code}/PLN</p>
          <p className="text-xl font-semibold tracking-tight tabular-nums">{formatRate(entry.rate)}</p>
          <p className="text-muted-foreground text-xs">
            {t('fx.effectiveDate', { date: formatDate(entry.effectiveDate) })}
          </p>
        </div>
        <ChangeChip value={entry.change30d} />
      </div>
      {chartData.length > 1 && (
        <ChartContainer config={config} className="aspect-auto h-12 w-full">
          <LineChart data={chartData} margin={{ top: 4, right: 2, bottom: 2, left: 2 }}>
            <Line
              type="monotone"
              dataKey="rate"
              stroke="var(--color-rate)"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ChartContainer>
      )}
    </div>
  );
}

export function FxRatesCard() {
  const [rates, setRates] = useState<FxRatesResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const { getRates } = useFxApi();

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await getRates([...CODES], 30);
      setRates(data);
      setStatus('ready');
    } catch (error) {
      console.error('Failed to load FX rates:', error);
      setStatus('error');
    }
  }, [getRates]);

  useEffect(() => {
    load();
  }, [load]);

  const codes = rates ? CODES.filter((c) => rates[c]) : [];

  return (
    <Card className="py-4">
      <CardHeader className="px-4">
        <CardTitle className="text-sm font-medium">{t('fx.title')}</CardTitle>
      </CardHeader>
      <CardContent className="px-4">
        {status === 'loading' && (
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        )}
        {status === 'error' && (
          <div className="text-muted-foreground flex items-center gap-2 py-2 text-sm">
            <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
            {t('fx.loadError')}
          </div>
        )}
        {status === 'ready' && codes.length === 0 && (
          <p className="text-muted-foreground py-2 text-sm">{t('fx.empty')}</p>
        )}
        {status === 'ready' && codes.length > 0 && rates && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {codes.map((code) => (
              <CurrencyPanel key={code} code={code} entry={rates[code]} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
